import { datadogRum } from '@datadog/browser-rum';
import { createClient as createWSClient } from 'graphql-ws';
import { subscriptionExchange } from 'urql';

import { globalVariables } from 'lib/globalVariables';

import { refetchActiveQueries } from './activeQueries';
import { AUTH_CONFIG } from './auth';
import { createLiveUpdatesNotice } from './liveUpdatesNotice';
import { waitToReconnect } from './reconnect';
import { getSocketCloseCode } from './socketClose';

import type { Exchange } from 'urql';

const WS_URI = globalVariables.getEnvironmentVariables().subscriptionURI;

// Without pings, RUM shows sockets dropping 300–420s apart, the shape of a load balancer idle
// timeout. 30s stays under the usual ones (ALB 60s, NLB 350s).
const KEEP_ALIVE_MS = 30_000;

// 1000/1001 are clean closes (navigation, unmount, or the lazy client idling).
const isCleanClose = (code: number | undefined) => code === 1000 || code === 1001;

// graphql-ws hands a subscription only the closes it won't retry itself. These can clear on a
// fresh connection (a server error, a race); every other one would fail the same way again.
const RESUBSCRIBE_CLOSE_CODES = new Set([1011, 4409, 4429, 4500]);
// connectionParams fetches a fresh token per connection, so one more try can succeed.
const UNAUTHORIZED_CLOSE_CODE = 4401;

// A subscription that stayed up this long before closing starts its backoff again.
const STABLE_SUBSCRIPTION_MS = KEEP_ALIVE_MS;

export function createSubscriptionClient(): Exchange {
  return input => {
    const notice = createLiveUpdatesNotice();
    let droppedAt: number | undefined;
    let activeSubscriptions = 0;

    const wsClient = createWSClient({
      url: WS_URI,
      connectionParams: async () => {
        const token = (await AUTH_CONFIG.getToken()) ?? null;
        if (!token) return {};
        return { Authorization: token };
      },
      keepAlive: KEEP_ALIVE_MS,
      // Retries never give up, since a subscription that errors stays dead until remount.
      // waitToReconnect holds them while nobody is looking at the page.
      retryAttempts: Infinity,
      retryWait: waitToReconnect,
      // Transport-level failures never surface as a urql operation result, so
      // without these handlers a broken subscription socket (failed handshake,
      // auth rejection, dropped connection) is completely invisible.
      on: {
        connected: () => {
          notice.clearPaused();
          if (droppedAt === undefined) return;
          datadogRum.addAction('graphql-ws reconnected', { downtime_ms: Date.now() - droppedAt });
          droppedAt = undefined;
          // Subscription events sent while the socket was down are gone, so the cache may be stale.
          refetchActiveQueries(input.client, () => true);
        },
        error: error => {
          datadogRum.addError(error, { source: 'graphql-ws', kind: 'connection_error' });
        },
        closed: event => {
          const code = getSocketCloseCode(event);
          if (isCleanClose(code) || droppedAt !== undefined || activeSubscriptions === 0) return;
          droppedAt = Date.now();
          notice.reconnecting();
          // 4499 is graphql-ws's synthetic "Terminated" code for a frozen socket it
          // reconnects from, so it's recoverable noise, not a failure.
          if (code !== undefined && code !== 4499) {
            datadogRum.addError(new Error(`GraphQL WS closed unexpectedly (${code})`), {
              source: 'graphql-ws',
              kind: 'connection_closed',
              code,
            });
          }
        },
      },
    });

    // With nothing subscribed, graphql-ws abandons any reconnect, so there is no outage to report.
    const onIdle = () => {
      droppedAt = undefined;
      notice.clearPaused();
    };

    return subscriptionExchange({
      forwardSubscription(request) {
        const query = { ...request, query: request.query || '' };
        return {
          subscribe: sink => {
            // graphql-ws holds a lock per subscription until its disposer runs, even after it
            // errors, and the lazy socket stays open while any lock is held.
            let dispose = () => {};
            let ended = false;
            let startedAt = 0;
            let failures = 0;
            let lastCode: number | undefined;

            const end = () => {
              if (ended) return;
              ended = true;
              dispose();
              activeSubscriptions -= 1;
              if (activeSubscriptions === 0) onIdle();
            };

            const shouldResubscribe = (code: number) => {
              if (code === UNAUTHORIZED_CLOSE_CODE) return !(failures > 0 && lastCode === code);
              return RESUBSCRIBE_CLOSE_CODES.has(code);
            };

            const start = () => {
              startedAt = Date.now();
              dispose = wsClient.subscribe(query, {
                ...sink,
                complete: () => {
                  end();
                  sink.complete();
                },
                error: error => {
                  if (ended) return;
                  const code = getSocketCloseCode(error);
                  if (code === undefined) {
                    end();
                    sink.error(error);
                    return;
                  }
                  failures = Date.now() - startedAt >= STABLE_SUBSCRIPTION_MS ? 0 : failures + 1;
                  if (!shouldResubscribe(code)) {
                    notice.stopped();
                    end();
                    sink.error(error);
                    return;
                  }
                  lastCode = code;
                  dispose();
                  void waitToReconnect(failures).then(() => {
                    if (!ended) start();
                  });
                },
              });
            };

            activeSubscriptions += 1;
            start();

            return { unsubscribe: end };
          },
        };
      },
    })(input);
  };
}
